/**
 * Service Worker Registration
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R13
 *
 * Handles service worker registration and lifecycle management.
 * Provides utilities for checking updates and clearing cache.
 *
 * FRONTEND-WEB-V1-R13: Register and manage service worker for offline capability.
 *
 * Risk Links: R11
 * FMEA Links: FM11
 */

type ServiceWorkerConfig = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
};

/**
 * Check if service workers are supported in the current environment.
 */
export function isServiceWorkerSupported(): boolean {
  return 'serviceWorker' in navigator;
}

/**
 * Register the service worker.
 *
 * FRONTEND-WEB-V1-R13: Initialize service worker for offline support.
 *
 * @param config - Optional callbacks for registration events
 */
export async function registerServiceWorker(
  config?: ServiceWorkerConfig
): Promise<ServiceWorkerRegistration | null> {
  if (!isServiceWorkerSupported()) {
    console.log('[SW Registration] Service workers not supported');
    return null;
  }

  // Only register in production or when explicitly enabled
  const isDev = import.meta.env.DEV;
  const enableInDev = import.meta.env['VITE_ENABLE_SW_DEV'] === 'true';

  if (isDev && !enableInDev) {
    console.log('[SW Registration] Skipping in development');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('[SW Registration] Registered successfully');

    // Check for updates on registration
    registration.addEventListener('updatefound', () => {
      const installingWorker = registration.installing;
      if (!installingWorker) return;

      installingWorker.addEventListener('statechange', () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New content is available; callback for update notification
            console.log('[SW Registration] New content available');
            config?.onUpdate?.(registration);
          } else {
            // Content cached for first time
            console.log('[SW Registration] Content cached for offline use');
            config?.onSuccess?.(registration);
          }
        }
      });
    });

    // Handle controller change (when new SW takes over)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[SW Registration] Controller changed');
    });

    return registration;
  } catch (error) {
    console.error('[SW Registration] Registration failed:', error);
    config?.onError?.(error as Error);
    return null;
  }
}

/**
 * Unregister all service workers.
 *
 * FRONTEND-WEB-V1-R13: Allow cleanup of service workers.
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!isServiceWorkerSupported()) {
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const results = await Promise.all(
      registrations.map((registration) => registration.unregister())
    );
    console.log('[SW Registration] Unregistered all service workers');
    return results.every(Boolean);
  } catch (error) {
    console.error('[SW Registration] Unregister failed:', error);
    return false;
  }
}

/**
 * Check for service worker updates.
 *
 * FRONTEND-WEB-V1-R13: Allow manual update checks.
 */
export async function checkForUpdates(): Promise<void> {
  if (!isServiceWorkerSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.update();
    console.log('[SW Registration] Update check completed');
  } catch (error) {
    console.error('[SW Registration] Update check failed:', error);
  }
}

/**
 * Skip waiting and activate new service worker immediately.
 *
 * FRONTEND-WEB-V1-R13: Allow immediate activation of updates.
 */
export function skipWaiting(): void {
  if (!isServiceWorkerSupported()) {
    return;
  }

  void navigator.serviceWorker.ready.then((registration) => {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  });
}

/**
 * Clear all service worker caches.
 *
 * FRONTEND-WEB-V1-R13: Allow cache cleanup.
 */
export async function clearCache(): Promise<boolean> {
  if (!isServiceWorkerSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    registration.active?.postMessage({ type: 'CLEAR_CACHE' });

    // Also clear from main thread
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames
        .filter((name) => name.startsWith('galen-portal-'))
        .map((name) => caches.delete(name))
    );

    console.log('[SW Registration] Cache cleared');
    return true;
  } catch (error) {
    console.error('[SW Registration] Cache clear failed:', error);
    return false;
  }
}

/**
 * Get the current service worker registration status.
 *
 * FRONTEND-WEB-V1-R13: Provide registration status for UI.
 */
export async function getRegistrationStatus(): Promise<{
  registered: boolean;
  active: boolean;
  waiting: boolean;
  installing: boolean;
}> {
  if (!isServiceWorkerSupported()) {
    return {
      registered: false,
      active: false,
      waiting: false,
      installing: false,
    };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    return {
      registered: !!registration,
      active: !!registration?.active,
      waiting: !!registration?.waiting,
      installing: !!registration?.installing,
    };
  } catch {
    return {
      registered: false,
      active: false,
      waiting: false,
      installing: false,
    };
  }
}
